import React, { useState } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonChip,
  IonIcon,
} from '@ionic/react';
import { refreshOutline, chevronUpOutline, chevronDownOutline } from 'ionicons/icons';
import { useParameters } from '../../services/parameters/parameters-provider';
import { useTags } from '../../services/tags/tags-provider';
import { useInferences } from './use-inferences';
import { useRecordings } from '../../services/RecordingsProvider';
import { useClients, ClientRecord, ReportRecord } from '../../services/client-records/clients-provider';
import { SurveyAnswers } from '../../components/kyc-editor/question-types';
import { InsightCard } from './insight-card';
import { SurveyData } from '../../services/parameters/parameters-schema';

export const StrategyRecommendationContent: React.FC<{report: ReportRecord}> = ({ report }) => {
  // State
  const [inferenceResults, setInferenceResults] = useState<any>(null);
  const [showGreenIssues, setShowGreenIssues] = useState(false);
  
  // Hooks
  const {inferResults, issueFlaggerTables, issueTriageTable} = useParameters();
  const {tags} = useTags();
  const {riskRecommendation} = useInferences(report);
  const {filesList} = useRecordings();
  const {myClients} = useClients();
  
  // Derived data
  const matchingClient = myClients.find(client=>client.id === report.clientId) as undefined | (ClientRecord & Partial<SurveyAnswers>);
  const morningstarData = report.morningstarData;
  const surveyData = matchingClient as SurveyData;
  const filteredRecordings = filesList.filter((file)=>JSON.stringify(file.customMetadata).toLowerCase().includes(matchingClient?.fullName.toLowerCase() || "[No client name found.]"));
  
  // Handler
  const handleAskJunior = () => {
    if (!morningstarData || !surveyData || !issueTriageTable) return;
    const results = inferResults(morningstarData, surveyData);
    setInferenceResults(results);
  };

  // Group issues by color
  const redIssues = inferenceResults?.triageOrder?.filter((id: string) => 
    inferenceResults.issueOutputs[id]?.lightStatus === 'red'
  ) || [];
  const yellowIssues = inferenceResults?.triageOrder?.filter((id: string) => 
    inferenceResults.issueOutputs[id]?.lightStatus === 'yellow'
  ) || [];
  const greenIssues = inferenceResults?.triageOrder?.filter((id: string) => 
    inferenceResults.issueOutputs[id]?.lightStatus === 'green'
  ) || [];

  // Render function for individual issues
  const renderIssue = (issueId: string) => {
    const output = inferenceResults.issueOutputs[issueId];
    const table = issueFlaggerTables.find(t => t.id === issueId);
    return (
      <InsightCard 
        key={issueId}
        recommendation={{
          title: table?.displayTitle || issueId,
          description: output.explanation || '',
          lightColor: output.lightStatus || 'yellow',
          tags: output.tags || []
        }}
      />
    );
  };

  return (
    <>
      {/* Ask Junior Button Section */}
      <IonCard>
        <IonCardHeader>
          <IonCardTitle>Suggested Talking Points</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonButton onClick={handleAskJunior}>
            {inferenceResults && <IonIcon icon={refreshOutline} slot="start" />}
            Ask Junior
          </IonButton>
          {!morningstarData && <p>Upload Morningstar report to enable analysis</p>}
          {!issueTriageTable && <p>Analysis parameters not configured. Contact administrator.</p>}
        </IonCardContent>
      </IonCard>

      {/* Strategy Menu Section */}
      {inferenceResults?.strategyOutput && (
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Recommended Strategies</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {inferenceResults.strategyOutput.explanation && (
              <p>{inferenceResults.strategyOutput.explanation}</p>
            )}
            {inferenceResults.strategyOutput.strategyMenu?.map((tagId: string) => {
              const tag = tags?.find(t => t.id === tagId && t.isProduct);
              return tag ? (
                <div key={tagId} style={{marginBottom: '1em'}}>
                  <strong>{tag.displayName}</strong>
                  {tag.description && <p>{tag.description}</p>}
                </div>
              ) : null;
            })}
            {inferenceResults.strategyOutput.extraTags?.length > 0 && (
              <div style={{marginTop: '1em'}}>
                {inferenceResults.strategyOutput.extraTags.map((tagId: string) => {
                  const tag = tags?.find(t => t.id === tagId);
                  return tag ? <IonChip key={tagId}>{tag.displayName}</IonChip> : null;
                })}
              </div>
            )}
          </IonCardContent>
        </IonCard>
      )}

      {/* Portfolio Analysis Section */}
      {inferenceResults && (
        <>
          <h3>Portfolio Analysis</h3>
          
          {/* Red Issues */}
          {redIssues.map(renderIssue)}
          
          {/* Yellow Issues */}
          {yellowIssues.map(renderIssue)}
          
          {/* Collapsible Green Issues */}
          {greenIssues.length > 0 && (
            <IonCard>
              <IonCardHeader 
                onClick={() => setShowGreenIssues(!showGreenIssues)}
                style={{cursor: 'pointer'}}
              >
                <IonCardTitle style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>Other analyzed factors (green)</span>
                  <IonIcon icon={showGreenIssues ? chevronUpOutline : chevronDownOutline} />
                </IonCardTitle>
              </IonCardHeader>
              {showGreenIssues && (
                <IonCardContent>
                  {greenIssues.map(renderIssue)}
                </IonCardContent>
              )}
            </IonCard>
          )}
          
          {/* Hardcoded Risk Recommendation */}
          {riskRecommendation && <InsightCard recommendation={riskRecommendation}/>}
        </>
      )}

      {/* Meeting Insights Section */}
      {inferenceResults && (
        <>
          <h3>Insights from Recent Meetings</h3>
          {filteredRecordings.map((meeting, index) => (
            <IonCard key={index} style={{ marginBottom: "1em", maxHeight: "15em", overflow:"auto" }} routerLink="/smart-notes">
              <IonCardHeader>
                {meeting.customMetadata?.date && 
                  <IonCardTitle>{new Date(Number(meeting.customMetadata?.date)).toLocaleString()}</IonCardTitle>
                }
              </IonCardHeader>
              <IonCardContent>
                {meeting.customMetadata?.summary.split("\n").map((paragraph, index) => <p key={index}>{paragraph}</p>)}
              </IonCardContent>
            </IonCard>
          ))}
        </>
      )}
    </>
  );
};
